import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useState } from "react";
import { createBuilding, fetchBuildings, updateBuilding } from "../../../redux/slices/buildingsSlice";
import toast from "react-hot-toast";
import Modal from "../../../components/modal/Modal";
import Form from "../../../components/form/Form";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { buildingSchema } from "../../../constants/yup.schema";
import { fetchGuards } from "../../../redux/slices/guardSlice";
import { routeNames } from "../../../constants/constants";
import { getAuthTokenData } from "../../../utils/tokenUtils";
import { FaShieldAlt, FaUser } from "react-icons/fa";
import { SetTable } from "@/components/SetTable";
import { Button } from "@/components/ui/button";
import { BUILDINGS_FORM } from "constants/building.form.constants";
import SimpleCard from "@/components/simple-card";
import { fetchTenants } from "../../../redux/slices/tenantSlice";
import { AlertTriangle, User } from "lucide-react";
import { getRemainingLinceces } from "../../../redux/slices/adminSlice";

const options = {
  type: [
    { label: "Residential", value: "Residential" },
    { label: "Commercial", value: "Commercial" },
  ],
};
const Buildings: React.FC = () => {
  const [selectedBuildingId, setSelectedBuildingId] = useState<string>("");
  const { buildings } = useSelector((state: RootState) => state.buildings);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [isEditBuildingModal, setIsEditBuildingModal] = useState<boolean>(false);
  const toggleEditBuildingModal = () => setIsEditBuildingModal(!isEditBuildingModal);
  const { tenants } = useSelector((state: RootState) => state.tenants);
  const navigate = useNavigate();
  const { userId, role } = getAuthTokenData();
  const dispatch = useDispatch<AppDispatch>();
  const guards = useSelector((state: RootState) => state.guards?.data?.data);
  const { remainingLicenses, totalFlats, licenses } = useSelector((state: RootState) => state.admin);
  const handleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  useEffect(() => {
    dispatch(fetchTenants({ role: "Tenant", ownerId: userId }));
    dispatch(fetchGuards({ query: userId }));
  }, [dispatch]);
  useEffect(() => {
    if (!userId || !role) return;
    dispatch(getRemainingLinceces({ adminId: userId }))
    dispatch(fetchBuildings({ query: userId })).catch(() => {
      toast.error("Failed to fetch buildings.");
    });
  }, [dispatch, userId]);
  const handleViewGuards = (item: any) => {
    navigate(routeNames.guards, {
      state: { buildingId: item._id },
    });
  };

  const handleViewTenant = (item: any) => {
    navigate(routeNames.tenantsManagement, {
      state: { buildingId: item._id },
    });
  };
  const handleViewBuildingDetails = (item: any) => {
    navigate(`${routeNames.buildingDetails}/${item._id}`, {
      state: { building: item },
    });
  };

  // Form configuration
  const defaultValues = {
    name: "",
    location: {
      state: "",
      address: "",
      city: "",
      country: "",
    },
  };


  const methods = useForm({
    defaultValues,
    resolver: yupResolver(buildingSchema)
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = methods;

  // Submit handler
  const onSubmit = async (data: any) => {
    console.log(data);
    // alert("Not implemented yet");
    // return
    const { name, location } = data;

    const buildingInfo = {
      name,
      location,
    };
    try {
      if (isEdit && selectedBuildingId) {
        await dispatch(updateBuilding({ id: selectedBuildingId, updatedBuilding: buildingInfo })).unwrap();
        toast.success("Building updated successfully!");
      } else {
        const newBuilding = { ...buildingInfo, ownerId: userId };
        await dispatch(createBuilding(newBuilding));
        toast.success("Building created successfully!");
      }
      dispatch(fetchBuildings({ query: userId }))
      handleModal();
      reset(defaultValues);
    } catch (error: any) {
      toast.error(
        `Failed to ${isEdit ? "update" : "create"} building: ${error.message}`
      );
    } finally {
    }
  };


  const handleEditBuilding = (item: any) => {
    setIsEdit(true);
    handleModal();
    setSelectedBuildingId(item._id);
    reset({
      name: item.name,
      location: {
        state: item.location.state,
        address: item.location.address,
        city: item.location.city,
        country: item.location.country,
      },

    });
  };
  const buildingtableKeys = [

    {
      name: "name",
    },
    {
      address: "location.address",
    },
    {
      City: "location.city",
    },
    {
      owner: "owner.name",
    },
  ]
  const actions = [
    {
      label: "View Guards",

      handler: (row: any) => handleViewGuards(row),
    },
    {
      label: "View Tenants",
      handler: (row: any) => handleViewTenant(row),
    },
    {
      label: "View Details",
      handler: (row: any) => handleViewBuildingDetails(row),
    },
    {
      label: "Edit Building",
      variant: "secondary",
      handler: (row: any) => handleEditBuilding(row),
    },
  ];
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 mt-6">
        <SimpleCard title="Total Buildings" number={buildings?.length} description="+2% from last month" Icon={FaUser} />
        {tenants && <SimpleCard title="Total Tenants" number={tenants?.length} description="+2% from last month" Icon={User} />}
        {guards && <SimpleCard title="Total Guards" number={guards?.length} description="+2% from last month" Icon={FaShieldAlt} />}
        <SimpleCard title="Total License" number={licenses} description="+12% from last hour" Icon={AlertTriangle} />
        <SimpleCard title="Remaing License" number={remainingLicenses} description="+12% from last hour" Icon={AlertTriangle} />
      </div>
      <div>
        <div className="mt-10 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">Buildings</h2>
          <Button variant="outline" onClick={() => navigate(routeNames.addBuilding)}>
            Add New Building
          </Button>
        </div>
        <div className="my-6 flex flex-col gap-2 bg-light_gray rounded-lg p-4">
          {buildings.length > 0
            ?
            <SetTable data={buildings} tableKeys={buildingtableKeys} actions={actions} />
            : "No buildings found"}
        </div>
      </div>
      <Modal
        isModalOpen={isModalOpen}
        onClick={handleModal}
        title={isEdit ? "Edit Building" : "Add a New Building"}
      >
        <Form
          fields={BUILDINGS_FORM}
          options={options}
          control={methods.control}
          method={methods}
          onClick={handleSubmit((data) => onSubmit(data))}
          errors={errors}
          isSubmitting={isSubmitting}
          style="grid grid-cols-2 gap-4"
        />
      </Modal>

    </>
  );
};

export default Buildings;
